import { useEffect, useRef, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { authApi, safeReturnTo, type EnrolMaterial } from './api';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';

type State = 'login' | 'code' | 'recovery' | 'enrol' | 'codes' | 'password';

const NETWORK_ERROR = 'Network error. Please check your connection and try again.';

/** Sign-in with the same six states and server contract as the audited
 *  login page: password, authenticator code, recovery code, first-time
 *  enrolment with a locally drawn QR, recovery codes shown once, and a
 *  personal password after the shared starter password. */
export function LoginPage() {
  const params = new URLSearchParams(window.location.search);
  const [state, setState] = useState<State>('login');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [changeMode, setChangeMode] = useState(params.get('change') === '1');
  const [pendingSetPassword, setPendingSetPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(false);
  const [recovery, setRecovery] = useState('');
  const [material, setMaterial] = useState<EnrolMaterial | null>(null);
  const [qrSvg, setQrSvg] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const lastAutoCode = useRef('');

  const returnTo = safeReturnTo(params.get('returnTo'));

  const fail = (err: unknown, fallback: string) => {
    if (err instanceof ApiError) setError(err.message || fallback);
    else setError(NETWORK_ERROR);
  };

  const finish = () => {
    if (pendingSetPassword || changeMode) {
      setState('password');
      return;
    }
    window.location.assign(returnTo);
  };

  const route = (next: string | undefined) => {
    if (next === 'code') setState('code');
    else if (next === 'enrol') void startEnrol();
    else finish();
  };

  async function startEnrol(given?: EnrolMaterial) {
    setError('');
    try {
      const m = given ?? (await authApi.enrol());
      setMaterial(m);
      setQrSvg(await QRCode.toString(m.uri, { type: 'svg', margin: 2, errorCorrectionLevel: 'M' }));
      setCode('');
      setState('enrol');
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'preauth' || err.code === 'state')) {
        setError(err.message || 'Your sign-in timed out. Please start again.');
        setState('login');
        return;
      }
      fail(err, 'Could not start the authenticator set-up.');
    }
  }

  async function submitLogin(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!email.trim() || !email.includes('@') || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setBusy(true);
    try {
      const r = await authApi.login(email.trim(), password);
      setPendingSetPassword(!!r.setPassword);
      route(r.next || 'done');
    } catch (err) {
      fail(err, 'Sign in failed. Please try again.');
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(value: string) {
    setError('');
    setBusy(true);
    try {
      await authApi.verify(value, trust);
      finish();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'enrol') {
        void startEnrol();
        return;
      }
      if (err instanceof ApiError && err.code === 'preauth') {
        setError('Your sign-in timed out. Please start again.');
        setState('login');
        return;
      }
      fail(err, 'That code was not accepted.');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  // Six digits typed: submit without waiting for the button.
  useEffect(() => {
    if (state === 'code' && code.length === 6 && code !== lastAutoCode.current && !busy) {
      lastAutoCode.current = code;
      void submitCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, state]);

  async function submitRecovery(e: FormEvent) {
    e.preventDefault();
    setError('');
    const value = recovery.trim();
    if (!value) {
      setError('Enter one of your recovery codes.');
      return;
    }
    setBusy(true);
    try {
      const r = await authApi.recover(value);
      setPendingSetPassword(!!r.setPassword);
      await startEnrol(r);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'preauth') {
        setError('Your sign-in timed out. Please start again.');
        setState('login');
        return;
      }
      fail(err, 'That recovery code was not accepted.');
      setRecovery('');
    } finally {
      setBusy(false);
    }
  }

  async function submitEnrol(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (code.length !== 6) {
      setError('Enter the 6-digit code the app shows.');
      return;
    }
    setBusy(true);
    try {
      const r = await authApi.confirm(code, trust);
      setRecoveryCodes(r.recoveryCodes || []);
      setState('codes');
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'preauth' || err.code === 'state')) {
        setError(err.message || 'Your sign-in timed out. Please start again.');
        setState('login');
        return;
      }
      if (err instanceof ApiError && err.code === 'no_pending') {
        void startEnrol();
        return;
      }
      fail(err, 'That code did not match.');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (newPassword.length < 10) {
      setError('Your new password must be at least 10 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match.');
      setConfirmPassword('');
      return;
    }
    if (newPassword === password) {
      setError('Please choose a different password from the one you just used.');
      return;
    }
    setBusy(true);
    try {
      await authApi.changePassword(password, newPassword);
      window.location.assign(returnTo);
    } catch (err) {
      fail(err, 'Could not save the password. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const digits = (v: string) => v.replace(/\D/g, '').slice(0, 6);
  const codesText = `Recovery codes for ${email} (each works once)\n${recoveryCodes.join('\n')}\n`;

  return (
    <div className="hud-grid flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-[420px] rounded-panel border border-hairline bg-raised p-8 shadow-elev-2">
        <div className="mb-6 flex flex-col items-center gap-3">
          <img src="/images/entarian-logo.svg" alt="Entarian" className="h-7" />
          <div className="text-center">
            <div className="font-display text-lg font-semibold tracking-wide">GEM DT</div>
            <div className="text-xs text-ink-3">Ground Enterprise Mission Digital Twin</div>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-4 rounded-control border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-ink"
          >
            {error}
          </div>
        )}

        {state === 'login' && (
          <form onSubmit={submitLogin} className="flex flex-col gap-3" noValidate>
            {changeMode && (
              <p className="text-sm text-ink-2">Sign in with your current password, then choose a new one.</p>
            )}
            <Input
              type="email"
              autoComplete="username"
              placeholder="Email"
              value={email}
              onChange={setEmail}
              autoFocus
            />
            <Input
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={setPassword}
            />
            <Primary busy={busy} label="Sign in" busyLabel="Signing in..." />
            {!changeMode && (
              <button
                type="button"
                className="text-left text-xs text-ink-3 hover:text-ink"
                onClick={() => setChangeMode(true)}
              >
                Change password
              </button>
            )}
          </form>
        )}

        {state === 'code' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (code.length === 6) void submitCode(code);
              else setError('Enter the 6-digit code from your authenticator app.');
            }}
            className="flex flex-col gap-3"
            noValidate
          >
            <Heading title="Authenticator code" text="Enter the 6-digit code from your authenticator app." />
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={code}
              onChange={(v) => setCode(digits(v))}
              mono
              autoFocus
            />
            <Trust checked={trust} onChange={setTrust} />
            <Primary busy={busy} label="Verify" busyLabel="Checking..." />
            <div className="flex justify-between text-xs text-ink-3">
              <button
                type="button"
                className="hover:text-ink"
                onClick={() => {
                  setError('');
                  setState('recovery');
                }}
              >
                Lost your phone? Use a recovery code
              </button>
              <button
                type="button"
                className="hover:text-ink"
                onClick={() => {
                  setError('');
                  setState('login');
                }}
              >
                Start over
              </button>
            </div>
          </form>
        )}

        {state === 'recovery' && (
          <form onSubmit={submitRecovery} className="flex flex-col gap-3" noValidate>
            <Heading
              title="Recovery code"
              text="Enter one of the recovery codes you saved when you set up your authenticator. It works once, and you will set up a new authenticator right after."
            />
            <Input placeholder="xxxx-xxxx-xxxx-xxxx" value={recovery} onChange={setRecovery} mono autoFocus />
            <Primary busy={busy} label="Sign in with recovery code" busyLabel="Checking..." />
            <button
              type="button"
              className="text-left text-xs text-ink-3 hover:text-ink"
              onClick={() => {
                setError('');
                setState('code');
              }}
            >
              Use the authenticator app instead
            </button>
          </form>
        )}

        {state === 'enrol' && material && (
          <form onSubmit={submitEnrol} className="flex flex-col gap-3" noValidate>
            <Heading title="Set up your authenticator" />
            <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-2">
              <li>
                Install <strong className="text-ink">Microsoft Authenticator</strong> (or Google
                Authenticator) on your phone.
              </li>
              <li>In the app, add an account and scan this code.</li>
              <li>Enter the 6-digit code the app shows.</li>
            </ol>
            <div
              className="mx-auto w-[180px] rounded-control bg-white p-2 [&_svg]:block [&_svg]:h-auto [&_svg]:w-full"
              dangerouslySetInnerHTML={{ __html: qrSvg }}
            />
            <div className="text-center text-xs text-ink-3">Cannot scan? Enter this key by hand:</div>
            <div className="break-all rounded-control bg-inset px-3 py-2 text-center font-mono text-xs tracking-wider text-ink-2">
              {material.secret.replace(/(.{4})/g, '$1 ').trim()}
            </div>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={code}
              onChange={(v) => setCode(digits(v))}
              mono
              autoFocus
            />
            <Trust checked={trust} onChange={setTrust} />
            <Primary busy={busy} label="Verify and continue" busyLabel="Checking..." />
          </form>
        )}

        {state === 'codes' && (
          <div className="flex flex-col gap-3">
            <Heading
              title="Save your recovery codes"
              text="If you lose your phone, any one of these signs you in once. Keep them somewhere safe; they are not shown again."
            />
            <div className="grid grid-cols-2 gap-1 rounded-control bg-inset p-3 font-mono text-sm">
              {recoveryCodes.map((c) => (
                <div key={c}>{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Secondary
                label={copied ? 'Copied' : 'Copy'}
                onClick={() => {
                  navigator.clipboard?.writeText(recoveryCodes.join('\n')).then(
                    () => setCopied(true),
                    () => setError('Copy failed. Please write the codes down.'),
                  );
                }}
              />
              <a
                className={secondaryClass}
                href={URL.createObjectURL(new Blob([codesText], { type: 'text/plain' }))}
                download="recovery-codes.txt"
              >
                Download
              </a>
            </div>
            <button
              type="button"
              className={primaryClass}
              onClick={() => {
                setError('');
                finish();
              }}
            >
              I have saved them, continue
            </button>
          </div>
        )}

        {state === 'password' && (
          <form onSubmit={submitPassword} className="flex flex-col gap-3" noValidate>
            <Heading
              title={pendingSetPassword ? 'Choose your password' : 'Change your password'}
              text={
                pendingSetPassword
                  ? 'You signed in with the shared starter password. Set your own to continue.'
                  : `Choose a new password for ${email}.`
              }
            />
            <Input
              type="password"
              autoComplete="new-password"
              placeholder="New password (at least 10 characters)"
              value={newPassword}
              onChange={setNewPassword}
              autoFocus
            />
            <Input
              type="password"
              autoComplete="new-password"
              placeholder="Repeat new password"
              value={confirmPassword}
              onChange={setConfirmPassword}
            />
            <Primary busy={busy} label="Save and continue" busyLabel="Saving..." />
          </form>
        )}

        <div className="mt-6 text-center text-[11px] text-ink-3">Entarian GEM DT, powered by UltiSim</div>
      </div>
    </div>
  );
}

const inputClass =
  'w-full rounded-control border border-hairline-strong bg-inset px-3 py-2.5 text-sm placeholder:text-ink-3 focus:border-accent focus:outline-none';
const primaryClass =
  'w-full rounded-control bg-accent px-3 py-2.5 text-sm font-semibold text-accent-ink transition hover:brightness-95 disabled:opacity-60';
const secondaryClass =
  'flex-1 rounded-control border border-hairline-strong px-3 py-2 text-center text-sm text-ink-2 transition hover:border-accent hover:text-ink';

function Input(props: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
  inputMode?: 'numeric' | 'text';
  mono?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <input
      className={cn(inputClass, props.mono && 'font-mono tracking-[0.3em]')}
      type={props.type || 'text'}
      autoComplete={props.autoComplete}
      inputMode={props.inputMode}
      placeholder={props.placeholder}
      aria-label={props.placeholder}
      value={props.value}
      autoFocus={props.autoFocus}
      onChange={(e) => props.onChange(e.target.value)}
    />
  );
}

function Primary({ busy, label, busyLabel }: { busy: boolean; label: string; busyLabel: string }) {
  return (
    <button type="submit" className={primaryClass} disabled={busy}>
      {busy ? busyLabel : label}
    </button>
  );
}

function Secondary({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={secondaryClass} onClick={onClick}>
      {label}
    </button>
  );
}

function Trust({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-xs text-ink-2">
      <input
        type="checkbox"
        className="accent-accent"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />{' '}
      Trust this browser for 30 days
    </label>
  );
}

function Heading({ title, text }: { title: string; text?: string }) {
  return (
    <div>
      <h3 className="font-display text-base font-semibold">{title}</h3>
      {text && <p className="mt-1 text-sm text-ink-2">{text}</p>}
    </div>
  );
}
